import { useEffect, useRef, useState } from 'react';
import {
  draftAgreement,
  getAgreement,
  getReviewStatus,
  getSessionSigningStatus,
  MediatorServiceError,
  previewAgreement,
  requestSignatures,
  resolveCase,
  shareReview,
  updateAgreement,
  type Agreement,
  type AgreementContent,
  type SessionReviewStatus,
  type SessionSigningStatus,
} from '../api/mediatorService';
import { agreementToMarkdown } from '../lib/agreement-format';
import { copyText } from '../lib/clipboard';
import { AgreementGeneratingOverlay } from '../components/AgreementGeneratingOverlay';
import { PreviewFrame } from '../components/PreviewFrame';
import { DocManagementPanel } from '../components/DocManagementPanel';
import type { Case } from '../types/case';

interface Props {
  caseId: string;
  sessionId: string;
  accessToken: string;
  property: Case;
}

type State =
  | { status: 'idle'; agreement: Agreement | null }
  | { status: 'loading' }
  | { status: 'drafting'; previous: Agreement | null }
  | { status: 'error'; error: string; previous: Agreement | null };

const sectionHeader = (label: string) => (
  <div className="section-label mt-4 first:mt-0">{label}</div>
);

/** Working copy for edit mode: terms flattened to newline-joined strings so
    each section edits as one textarea. */
interface EditDraft {
  title: string;
  date: string;
  parties: Array<{ name: string; role: string }>;
  sections: Array<{ section: string; termsText: string }>;
  signers: Array<{ name: string; role: string }>;
  confidentiality: string;
}

const toEditDraft = (c: AgreementContent): EditDraft => ({
  title: c.title,
  date: c.date,
  parties: c.parties.map((p) => ({ ...p })),
  sections: c.agreed_terms.map((s) => ({ section: s.section, termsText: s.terms.join('\n') })),
  signers: c.signatures_required.map((s) => ({ ...s })),
  confidentiality: c.confidentiality_notice,
});

/** Rebuild content_json; empty term lines vanish, term-less sections drop.
    Returns null when nothing signable/agreeable remains. */
const fromEditDraft = (draft: EditDraft, base: AgreementContent): AgreementContent | null => {
  const agreed_terms = draft.sections
    .map((s) => ({
      section: s.section.trim(),
      terms: s.termsText.split('\n').map((t) => t.trim()).filter(Boolean),
    }))
    .filter((s) => s.section && s.terms.length > 0);
  const parties = draft.parties
    .map((p) => ({ name: p.name.trim(), role: p.role.trim() }))
    .filter((p) => p.name);
  const signatures_required = draft.signers
    .map((s) => ({ name: s.name.trim(), role: s.role.trim() }))
    .filter((s) => s.name);
  if (!draft.title.trim() || agreed_terms.length === 0 || signatures_required.length === 0 || parties.length === 0) {
    return null;
  }
  return {
    ...base,
    title: draft.title.trim(),
    date: draft.date.trim() || base.date,
    parties,
    agreed_terms,
    signatures_required,
    confidentiality_notice: draft.confidentiality.trim(),
  };
};

interface EditState {
  draft: EditDraft;
  /** Serialized snapshot at edit start — the dirty check. */
  initial: string;
  view: 'edit' | 'preview';
  preview: { html: string | null; loading: boolean; error: string | null };
  confirmDiscard: boolean;
  saving: boolean;
  error: string | null;
}

const startEdit = (c: AgreementContent): EditState => {
  const draft = toEditDraft(c);
  return {
    draft,
    initial: JSON.stringify(draft),
    view: 'edit',
    preview: { html: null, loading: false, error: null },
    confirmDiscard: false,
    saving: false,
    error: null,
  };
};

/** Textarea with tiny B/I buttons that wrap the current selection in the
    **bold** / *italic* markers the engine renders. */
function TermsTextarea({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const wrapSelection = (marker: string) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: start, selectionEnd: end } = el;
    if (start === end) return; // nothing selected — no-op
    const next =
      value.slice(0, start) + marker + value.slice(start, end) + marker + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + marker.length, end + marker.length);
    });
  };

  return (
    <div>
      <div className="mb-0.5 flex items-center gap-1">
        <button
          type="button"
          className="rounded border border-surface-border px-1.5 text-[11px] font-bold text-gray-600 hover:bg-gray-50"
          title="Bold selection"
          onMouseDown={(e) => {
            e.preventDefault();
            wrapSelection('**');
          }}
        >
          B
        </button>
        <button
          type="button"
          className="rounded border border-surface-border px-1.5 text-[11px] italic text-gray-600 hover:bg-gray-50"
          title="Italicize selection"
          onMouseDown={(e) => {
            e.preventDefault();
            wrapSelection('*');
          }}
        >
          I
        </button>
        <span className="text-[10px] text-gray-400">select text, then B / I</span>
      </div>
      <textarea
        ref={ref}
        className="input min-h-[4rem] w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** Name/role row list editor (used for both parties and signers). */
function NameRoleRows({
  rows,
  onChange,
  removeLabel,
  addLabel,
}: {
  rows: Array<{ name: string; role: string }>;
  onChange: (rows: Array<{ name: string; role: string }>) => void;
  removeLabel: string;
  addLabel: string;
}) {
  return (
    <>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input
            className="input w-full"
            value={r.name}
            placeholder="Name"
            onChange={(e) => {
              const next = rows.slice();
              next[i] = { ...r, name: e.target.value };
              onChange(next);
            }}
          />
          <input
            className="input w-full"
            value={r.role}
            placeholder="Role"
            onChange={(e) => {
              const next = rows.slice();
              next[i] = { ...r, role: e.target.value };
              onChange(next);
            }}
          />
          <button
            type="button"
            className="shrink-0 text-xs text-gray-400 hover:text-red-600"
            aria-label={removeLabel}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-xs font-medium text-brand hover:underline"
        onClick={() => onChange([...rows, { name: '', role: '' }])}
      >
        {addLabel}
      </button>
    </>
  );
}

const reviewChip = (effective: string) => {
  switch (effective) {
    case 'confirmed':
      return { cls: 'bg-green-100 text-green-800', label: 'Confirmed ✓' };
    case 'confirmed_stale':
      return { cls: 'bg-amber-100 text-amber-800', label: 'Confirmed (outdated)' };
    case 'changes_requested':
      return { cls: 'bg-red-100 text-red-800', label: 'Changes requested' };
    default:
      return { cls: 'bg-gray-100 text-gray-700', label: 'Pending' };
  }
};

export function AgreementPage({ sessionId, accessToken, property, caseId }: Props) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [copied, setCopied] = useState(false);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [resolveState, setResolveState] = useState<
    | { kind: 'idle' }
    | { kind: 'resolving' }
    | { kind: 'resolved'; caseStatus: string; romGenerated: boolean }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  const [signing, setSigning] = useState<SessionSigningStatus | null>(null);
  // AI-review acknowledgment (Danny, 2026-10-31): gates Request signatures.
  const [aiReviewed, setAiReviewed] = useState(false);
  // Settlement amount confirm at close-out (Danny, 2026-11-02): prefilled
  // from the agreement (parsed or AI-extracted), mediator confirms/corrects.
  const [settleAmount, setSettleAmount] = useState<string>('');
  const [settleSource, setSettleSource] = useState<string | null>(null);
  // Whether signing requests were actually DISPATCHED (agreement status
  // sent/signed), vs. signer rows merely existing on a draft. The "Sent"
  // chip must reflect this — a draft's pending signers were never sent to
  // (Rachel, 2026-10-08: the panel said "Sent" while nothing went out).
  const [requestsSent, setRequestsSent] = useState(false);
  const [signSend, setSignSend] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  // Copy-link feedback (Scott, 2026-09-21: a silently failed webview copy
  // pasted stale clipboard as a dead link). failed => show the URL to \u2318C.
  const [copiedSig, setCopiedSig] = useState<{ id: string; failed: boolean } | null>(null);
  const [signGate, setSignGate] = useState<{ pending: string[]; changes: string[] } | null>(null);
  const [review, setReview] = useState<SessionReviewStatus | null>(null);
  const [share, setShare] = useState<{ open: boolean; selected: string[]; busy: boolean; error: string | null }>({
    open: false,
    selected: [],
    busy: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    getAgreement(sessionId, accessToken)
      .then((agreement) => {
        if (cancelled) return;
        setState({ status: 'idle', agreement });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load agreement';
        setState({ status: 'error', error: message, previous: null });
      });
    getSessionSigningStatus(sessionId, accessToken)
      .then((res) => {
        if (!cancelled && res && res.signing.total > 0) {
          setSigning(res.signing);
          if (res.settlement && res.settlement.amount != null) {
            setSettleAmount((cur) => (cur === '' ? String(res.settlement!.amount) : cur));
            setSettleSource(res.settlement.source);
          }
          setRequestsSent(res.agreement_status === 'sent' || res.agreement_status === 'signed');
        }
      })
      .catch(() => undefined);
    getReviewStatus(sessionId, accessToken)
      .then((res) => {
        if (!cancelled && res && res.review.total > 0) setReview(res.review);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sessionId, accessToken]);

  // While signatures are out, poll so the button flips to "Close out
  // mediation" the moment the last party signs on their phone. While a
  // review is out (and signatures aren't), poll the review status the same
  // way so confirmations appear live.
  // "Signatures are out" means they were actually DISPATCHED (requestsSent),
  // not merely that draft signature rows exist: saving an agreement edit
  // reconciles signer rows (signing.total > 0) WITHOUT sending anything, and
  // that must not flip the Request-signatures button to a disabled "Waiting"
  // state (Rachel 2026-10-08 — the chip was fixed then, the button was not).
  const awaitingSignatures = requestsSent && !!signing && !signing.all_signed;
  const watchingReview = !requestsSent && !!review && review.total > 0;
  useEffect(() => {
    if ((!awaitingSignatures && !watchingReview) || resolveState.kind === 'resolved') return;
    const timer = window.setInterval(() => {
      if (awaitingSignatures) {
        getSessionSigningStatus(sessionId, accessToken)
          .then((res) => {
            if (res && res.signing.total > 0) {
              setSigning(res.signing);
              if (res.settlement && res.settlement.amount != null) {
                setSettleAmount((cur) => (cur === '' ? String(res.settlement!.amount) : cur));
                setSettleSource(res.settlement.source);
              }
              setRequestsSent(res.agreement_status === 'sent' || res.agreement_status === 'signed');
            }
          })
          .catch(() => undefined);
      } else {
        getReviewStatus(sessionId, accessToken)
          .then((res) => {
            if (res && res.review.total > 0) setReview(res.review);
          })
          .catch(() => undefined);
      }
    }, 8000);
    return () => window.clearInterval(timer);
  }, [awaitingSignatures, watchingReview, resolveState.kind, sessionId, accessToken]);

  const handleRequestSignatures = async (confirmOverride = false) => {
    setSignSend({ busy: true, error: null });
    try {
      const { signing: next } = await requestSignatures(sessionId, accessToken, { confirmOverride });
      setSigning(next);
      setRequestsSent(true);
      setSignSend({ busy: false, error: null });
      setSignGate(null);
    } catch (err) {
      if (err instanceof MediatorServiceError && err.code === 'REVIEW_INCOMPLETE') {
        // Soft gate: surface who hasn't confirmed and offer "Send anyway".
        const latest = await getReviewStatus(sessionId, accessToken).catch(() => null);
        const reviews = latest?.review.reviews ?? [];
        setSignGate({
          pending: reviews
            .filter((r) => r.effective_status === 'pending' || r.effective_status === 'confirmed_stale')
            .map((r) => r.party_name ?? 'A party'),
          changes: reviews
            .filter((r) => r.effective_status === 'changes_requested')
            .map((r) => r.party_name ?? 'A party'),
        });
        setSignSend({ busy: false, error: null });
        return;
      }
      const message =
        err instanceof MediatorServiceError ? err.message : err instanceof Error ? err.message : 'Send failed';
      setSignSend({ busy: false, error: message });
    }
  };

  const currentAgreement =
    state.status === 'idle'
      ? state.agreement
      : state.status === 'drafting' || state.status === 'error'
        ? state.previous
        : null;

  const drafting = state.status === 'drafting';

  const handleDraft = async () => {
    setState({ status: 'drafting', previous: currentAgreement });
    const pressedAt = Date.now();
    try {
      // The Zoom webview can silently drop a response mid-flight (Scott,
      // 2026-09-21, 26S349: the draft completed server-side in 6 seconds
      // but the overlay never cleared, hiding the whole signing UI for
      // the rest of the mediation). After 25s assume the response is
      // lost and fall through to polling — the engine reuses a recent
      // draft on retry, so polling always converges on the finished doc.
      let res: Awaited<ReturnType<typeof draftAgreement>>;
      try {
        res = await Promise.race([
          draftAgreement(sessionId, accessToken),
          new Promise<{ status: 'generating' }>((resolve) =>
            setTimeout(() => resolve({ status: 'generating' }), 25_000),
          ),
        ]);
      } catch (err) {
        // A real engine refusal (no transcript, validation) surfaces
        // immediately; a network drop goes to polling like a timeout.
        if (err instanceof MediatorServiceError) throw err;
        res = { status: 'generating' };
      }
      if ('id' in res) {
        setState({ status: 'idle', agreement: res });
        return;
      }
      // Drafting runs server-side in the background — poll for the draft
      // (Opus typically lands in 30-90s; cap at 4 minutes).
      for (;;) {
        if (Date.now() - pressedAt > 240_000) {
          setState({
            status: 'error',
            error: 'Drafting is taking unusually long. Wait a moment and press Regenerate — a finished draft will be picked up, not duplicated.',
            previous: currentAgreement,
          });
          return;
        }
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const latest = await getAgreement(sessionId, accessToken);
          const freshEnough = (a: { created_at: string; updated_at?: string | null }) =>
            new Date(a.updated_at ?? a.created_at).getTime() >= pressedAt - 120_000;
          // Regeneration UPDATES the existing row (same id) — accept it
          // by freshness, not only by a new id.
          if (latest && freshEnough(latest) && (latest.id !== currentAgreement?.id || new Date((latest as { updated_at?: string | null }).updated_at ?? latest.created_at).getTime() >= pressedAt)) {
            setState({ status: 'idle', agreement: latest });
            return;
          }
        } catch (pollErr) {
          // The engine reports WHY the background draft failed on the 404
          // — stop polling and show it (was an infinite spinner).
          if (pollErr instanceof MediatorServiceError && pollErr.draftError) {
            setState({ status: 'error', error: pollErr.draftError, previous: currentAgreement });
            return;
          }
          // A 401 here means the Zoom token expired AND the refresh failed
          // (mediatorService auto-retries a recoverable 401) — the session is
          // dead, so stop polling instead of locking "Drafting…" for 4 min
          // (Rachel, 2026-10-08). Re-authing restores the panel.
          if (pollErr instanceof MediatorServiceError && pollErr.status === 401) {
            setState({ status: 'error', error: 'Your Zoom session expired. Reconnect (sign in again) and press Regenerate — a finished draft will be picked up, not duplicated.', previous: currentAgreement });
            return;
          }
          /* transient poll failure — keep waiting */
        }
      }
    } catch (err) {
      const message =
        err instanceof MediatorServiceError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to draft agreement';
      setState({ status: 'error', error: message, previous: currentAgreement });
    }
  };

  const handleResolve = async () => {
    if (!currentAgreement) return;
    setResolveState({ kind: 'resolving' });
    try {
      const amt = Number(settleAmount.replace(/[$,\s]/g, ''));
      const { case_status, rom_generated } = await resolveCase(sessionId, accessToken, {
        settlementAmount: Number.isFinite(amt) && amt > 0 ? amt : null,
      });
      setResolveState({ kind: 'resolved', caseStatus: case_status, romGenerated: !!rom_generated });
    } catch (err) {
      const message =
        err instanceof MediatorServiceError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Resolve failed';
      setResolveState({ kind: 'error', message });
    }
  };

  const editDirty = !!edit && JSON.stringify(edit.draft) !== edit.initial;

  const handleCancelEdit = () => {
    if (!edit) return;
    if (editDirty && !edit.confirmDiscard) {
      setEdit({ ...edit, confirmDiscard: true });
      return;
    }
    setEdit(null);
  };

  const handleSaveEdit = async () => {
    if (!edit || !currentAgreement) return;
    const content = fromEditDraft(edit.draft, currentAgreement.content_json);
    if (!content) {
      setEdit({ ...edit, error: 'Keep a title, at least one party, one term, and one signer.' });
      return;
    }
    setEdit({ ...edit, saving: true, error: null });
    try {
      const saved = await updateAgreement(sessionId, content, accessToken);
      setState({ status: 'idle', agreement: saved });
      setEdit(null);
      // An edit stales confirmations — refresh so chips flip immediately.
      if (review) {
        getReviewStatus(sessionId, accessToken)
          .then((res) => res && setReview(res.review))
          .catch(() => undefined);
      }
    } catch (err) {
      const message =
        err instanceof MediatorServiceError && err.code === 'STALE_EDIT'
          ? 'This draft changed in another window. Reload the sidebar and re-apply your edit.'
          : err instanceof MediatorServiceError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Save failed';
      setEdit({ ...edit, saving: false, error: message });
    }
  };

  const handlePreviewTab = async () => {
    if (!edit || !currentAgreement) return;
    const content = fromEditDraft(edit.draft, currentAgreement.content_json);
    if (!content) {
      setEdit({ ...edit, error: 'Keep a title, at least one party, one term, and one signer.' });
      return;
    }
    setEdit({ ...edit, view: 'preview', error: null, preview: { html: null, loading: true, error: null } });
    try {
      const html = await previewAgreement(sessionId, content, accessToken);
      setEdit((prev) =>
        prev ? { ...prev, preview: { html, loading: false, error: null } } : prev,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Preview failed';
      setEdit((prev) =>
        prev ? { ...prev, preview: { html: null, loading: false, error: message } } : prev,
      );
    }
  };

  const handleShareReview = async () => {
    if (share.selected.length === 0) {
      setShare({ ...share, error: 'Select at least one party.' });
      return;
    }
    setShare({ ...share, busy: true, error: null });
    try {
      const res = await shareReview(sessionId, share.selected, accessToken);
      setReview(res.review);
      setShare({ open: false, selected: [], busy: false, error: null });
      setState((prev) =>
        prev.status === 'idle' && prev.agreement
          ? { status: 'idle', agreement: { ...prev.agreement, status: res.agreement_status } }
          : prev,
      );
    } catch (err) {
      const message =
        err instanceof MediatorServiceError ? err.message : err instanceof Error ? err.message : 'Share failed';
      setShare({ ...share, busy: false, error: message });
    }
  };

  const handleCopy = async () => {
    if (!currentAgreement) return;
    const markdown = agreementToMarkdown(currentAgreement.content_json);
    const result = await copyText(markdown);
    if (result === 'failed') return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  // Roster for the share picker: the case's real parties (they have the
  // contact info), never the neutral.
  const shareableParties = property.parties.filter(
    (p) => p.role_archetype !== 'neutral' && p.role_archetype !== 'mediator',
  );

  if (state.status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-gray-500">
        Loading agreement…
      </div>
    );
  }

  // Drafting from scratch (no previous agreement) — show the live overlay.
  if (drafting && !currentAgreement) {
    return <AgreementGeneratingOverlay />;
  }

  // Empty state — no agreement yet
  if (!currentAgreement) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="text-3xl" aria-hidden>
          📝
        </div>
        <h2 className="text-base font-semibold">Draft the agreement</h2>
        <p className="max-w-sm text-sm text-gray-600">
          Generate a structured draft from the session&rsquo;s transcript, key moments, and case
          context. You can review, copy as markdown, and regenerate anytime.
        </p>
        <button
          type="button"
          className="btn-primary mt-2"
          onClick={handleDraft}
        >
          Draft agreement
        </button>
        {state.status === 'error' && (
          <div className="mt-2 text-xs text-red-600">{state.error}</div>
        )}
      </div>
    );
  }

  const c = currentAgreement?.content_json;
  const resolved = resolveState.kind === 'resolved';
  const resolving = resolveState.kind === 'resolving';

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-surface-border px-3 py-2">
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-wider text-gray-500">
            {resolved ? 'Resolved' : `Draft · ${currentAgreement?.status}`}
          </div>
          <div className="truncate text-sm font-semibold">{c?.title ?? 'Agreement'}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {edit ? (
            <>
              <button
                type="button"
                className="btn-secondary !py-1 !text-xs"
                onClick={handleCancelEdit}
                disabled={edit.saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary !py-1 !text-xs"
                onClick={() => void handleSaveEdit()}
                disabled={edit.saving}
              >
                {edit.saving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn-secondary !py-1 !text-xs"
                onClick={() => void handleCopy()}
                disabled={!currentAgreement}
              >
                {copied ? 'Copied ✓' : 'Copy markdown'}
              </button>
              <button
                type="button"
                className="btn-secondary !py-1 !text-xs"
                onClick={() => {
                  if (currentAgreement) setEdit(startEdit(currentAgreement.content_json));
                }}
                disabled={!currentAgreement || drafting || resolved || requestsSent}
                title={requestsSent ? 'Signature requests are out - regenerate to start a new draft' : undefined}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn-secondary !py-1 !text-xs"
                onClick={() => void handleDraft()}
                disabled={drafting || resolved}
              >
                {drafting ? 'Drafting…' : 'Regenerate'}
              </button>
            </>
          )}
        </div>
      </header>

      {resolved && (
        <div className="shrink-0 border-b border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
          <span className="font-semibold">✓ Mediation closed out.</span>{' '}
          <span className="opacity-80">
            Status: <span className="font-mono">{resolveState.caseStatus}</span>
            {resolveState.kind === 'resolved' && resolveState.romGenerated
              ? ' · Report of Mediation generated.'
              : ' · agreement archived.'}
          </span>
        </div>
      )}

      {resolveState.kind === 'error' && (
        <div className="shrink-0 border-b border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">
          {resolveState.message}
        </div>
      )}

      {state.status === 'error' && (
        <div className="shrink-0 border-b border-surface-border bg-red-50 px-3 py-1.5 text-xs text-red-700">
          {state.error}
        </div>
      )}

      {edit && (
        <div className="flex shrink-0 items-center gap-1 border-b border-surface-border px-3 py-1.5">
          <button
            type="button"
            className={
              edit.view === 'edit'
                ? 'rounded-md bg-brand-subtle px-2 py-0.5 text-xs font-semibold text-brand'
                : 'rounded-md px-2 py-0.5 text-xs text-gray-500 hover:text-gray-800'
            }
            onClick={() => setEdit({ ...edit, view: 'edit' })}
          >
            Edit
          </button>
          <button
            type="button"
            className={
              edit.view === 'preview'
                ? 'rounded-md bg-brand-subtle px-2 py-0.5 text-xs font-semibold text-brand'
                : 'rounded-md px-2 py-0.5 text-xs text-gray-500 hover:text-gray-800'
            }
            onClick={() => void handlePreviewTab()}
          >
            Preview
          </button>
          <span className="ml-auto text-[10px] text-gray-400">Preview shows exactly what parties see</span>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {edit && edit.view === 'preview' && (
          <div className="h-full min-h-[16rem]">
            {edit.preview.loading && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Rendering preview…
              </div>
            )}
            {edit.preview.error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
                {edit.preview.error}
              </div>
            )}
            {edit.preview.html && <PreviewFrame html={edit.preview.html} />}
          </div>
        )}
        {edit && edit.view === 'edit' && (
          <div className="space-y-3 text-sm">
            {edit.confirmDiscard && (
              <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                <span>Discard unsaved changes?</span>
                <span className="flex shrink-0 gap-2">
                  <button type="button" className="font-semibold hover:underline" onClick={() => setEdit(null)}>
                    Discard
                  </button>
                  <button
                    type="button"
                    className="hover:underline"
                    onClick={() => setEdit({ ...edit, confirmDiscard: false })}
                  >
                    Keep editing
                  </button>
                </span>
              </div>
            )}
            {edit.error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
                {edit.error}
              </div>
            )}
            {sectionHeader('Title')}
            <input
              className="input w-full"
              value={edit.draft.title}
              onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, title: e.target.value } })}
            />
            {sectionHeader('Date')}
            <input
              className="input w-full"
              type="date"
              value={edit.draft.date}
              onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, date: e.target.value } })}
            />
            {sectionHeader('Parties')}
            <NameRoleRows
              rows={edit.draft.parties}
              onChange={(parties) => setEdit({ ...edit, draft: { ...edit.draft, parties } })}
              removeLabel="Remove party"
              addLabel="+ Add party"
            />
            {sectionHeader('Agreed Terms')}
            <p className="text-xs text-gray-500">
              One term per line. Select text and use B / I for bold or italics.
            </p>
            {edit.draft.sections.map((s, i) => (
              <div key={i} className="space-y-1 rounded-md border border-surface-border p-2">
                <div className="flex items-center gap-1.5">
                  <input
                    className="input w-full font-semibold"
                    value={s.section}
                    placeholder="Section name"
                    onChange={(e) => {
                      const sections = edit.draft.sections.slice();
                      sections[i] = { ...s, section: e.target.value };
                      setEdit({ ...edit, draft: { ...edit.draft, sections } });
                    }}
                  />
                  <button
                    type="button"
                    className="shrink-0 text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
                    aria-label="Move section up"
                    disabled={i === 0}
                    onClick={() => {
                      const sections = edit.draft.sections.slice();
                      [sections[i - 1], sections[i]] = [sections[i], sections[i - 1]];
                      setEdit({ ...edit, draft: { ...edit.draft, sections } });
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="shrink-0 text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
                    aria-label="Move section down"
                    disabled={i === edit.draft.sections.length - 1}
                    onClick={() => {
                      const sections = edit.draft.sections.slice();
                      [sections[i], sections[i + 1]] = [sections[i + 1], sections[i]];
                      setEdit({ ...edit, draft: { ...edit.draft, sections } });
                    }}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="shrink-0 text-xs text-gray-400 hover:text-red-600"
                    aria-label="Remove section"
                    onClick={() =>
                      setEdit({
                        ...edit,
                        draft: { ...edit.draft, sections: edit.draft.sections.filter((_, j) => j !== i) },
                      })
                    }
                  >
                    ✕
                  </button>
                </div>
                <TermsTextarea
                  value={s.termsText}
                  onChange={(termsText) => {
                    const sections = edit.draft.sections.slice();
                    sections[i] = { ...s, termsText };
                    setEdit({ ...edit, draft: { ...edit.draft, sections } });
                  }}
                />
              </div>
            ))}
            <button
              type="button"
              className="text-xs font-medium text-brand hover:underline"
              onClick={() =>
                setEdit({
                  ...edit,
                  draft: {
                    ...edit.draft,
                    sections: [...edit.draft.sections, { section: '', termsText: '' }],
                  },
                })
              }
            >
              + Add section
            </button>
            {sectionHeader('Signers')}
            <p className="text-xs text-gray-500">Parties only — the mediator does not sign.</p>
            <NameRoleRows
              rows={edit.draft.signers}
              onChange={(signers) => setEdit({ ...edit, draft: { ...edit.draft, signers } })}
              removeLabel="Remove signer"
              addLabel="+ Add signer"
            />
            {sectionHeader('Confidentiality')}
            <textarea
              className="input min-h-[5rem] w-full"
              value={edit.draft.confidentiality}
              onChange={(e) => setEdit({ ...edit, draft: { ...edit.draft, confidentiality: e.target.value } })}
            />
          </div>
        )}
        {!edit && c && (
          <article className="space-y-3 text-sm">
            <div>
              <div className="text-xs text-gray-500">{c.date}</div>
              <h1 className="text-base font-semibold">{c.title}</h1>
            </div>

            {sectionHeader('Parties')}
            <ul className="space-y-0.5 text-sm">
              {c.parties.map((p, i) => (
                <li key={i}>
                  <span className="font-medium">{p.name}</span>
                  <span className="text-gray-500"> — {p.role}</span>
                </li>
              ))}
            </ul>

            {sectionHeader('Agreed Terms')}
            <div className="space-y-3">
              {c.agreed_terms.map((section, i) => (
                <div key={i}>
                  <div className="font-semibold text-gray-900">{section.section}</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-800">
                    {section.terms.map((term, j) => (
                      <li key={j}>{term}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {!resolved && !signing && (
              <>
                {sectionHeader('Party review')}
                {review && review.total > 0 && (
                  <ul className="space-y-2 text-sm">
                    {review.reviews.map((r) => {
                      const chip = reviewChip(r.effective_status);
                      return (
                        <li key={r.id}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="truncate font-medium">{r.party_name ?? 'Party'}</div>
                              {r.review_url && (
                                <button
                                  type="button"
                                  className="text-xs text-brand hover:underline"
                                  onClick={() => void copyText(r.review_url ?? '')}
                                >
                                  Copy review link
                                </button>
                              )}
                            </div>
                            <span
                              className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${chip.cls}`}
                            >
                              {chip.label}
                            </span>
                          </div>
                          {r.effective_status === 'changes_requested' && r.comment && (
                            <div className="mt-1 rounded-md border border-red-100 bg-red-50 px-2 py-1 text-xs text-red-900">
                              “{r.comment}”
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {share.open ? (
                  <div className="space-y-2 rounded-md border border-surface-border p-2">
                    <div className="text-xs font-medium text-gray-700">Send a live review link to:</div>
                    {shareableParties.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={share.selected.includes(p.id)}
                          onChange={(e) =>
                            setShare({
                              ...share,
                              selected: e.target.checked
                                ? [...share.selected, p.id]
                                : share.selected.filter((id) => id !== p.id),
                            })
                          }
                        />
                        <span className="min-w-0 truncate">
                          {p.name} <span className="text-gray-500">· {p.role}</span>
                        </span>
                        {!p.email && !p.phone && (
                          <span className="shrink-0 text-[10px] text-amber-600">no contact info</span>
                        )}
                      </label>
                    ))}
                    {share.error && <div className="text-xs text-red-600">{share.error}</div>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn-primary !py-1 !text-xs"
                        onClick={() => void handleShareReview()}
                        disabled={share.busy}
                      >
                        {share.busy ? 'Sending…' : 'Send review links'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary !py-1 !text-xs"
                        onClick={() => setShare({ ...share, open: false, error: null })}
                        disabled={share.busy}
                      >
                        Cancel
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-500">
                      Each party gets their own private link. The page updates live as you edit.
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="text-xs font-medium text-brand hover:underline"
                    onClick={() =>
                      setShare({
                        open: true,
                        selected: shareableParties.filter((p) => p.email || p.phone).map((p) => p.id),
                        busy: false,
                        error: null,
                      })
                    }
                    disabled={drafting}
                  >
                    {review && review.total > 0 ? 'Re-send review links…' : 'Share with parties for review…'}
                  </button>
                )}
              </>
            )}

            {sectionHeader('Signatures')}
            {signing ? (
              <ul className="space-y-2 text-sm">
                {signing.signers.map((sg) => (
                  <li key={sg.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-medium">
                        {sg.signer_name}
                        {sg.role_label ? <span className="text-gray-500"> — {sg.role_label}</span> : null}
                      </div>
                      {sg.status === 'pending' && sg.sign_url && (
                        <button
                          type="button"
                          className="text-xs text-brand hover:underline"
                          onClick={async () => {
                            const result = await copyText(sg.sign_url ?? '');
                            const failed = result === 'failed';
                            setCopiedSig({ id: sg.id, failed });
                            if (!failed) setTimeout(() => setCopiedSig((p) => (p?.id === sg.id ? null : p)), 4000);
                          }}
                        >
                          {copiedSig?.id === sg.id && !copiedSig.failed ? '✓ Copied' : 'Copy signing link'}
                        </button>
                      )}
                      {copiedSig?.id === sg.id && copiedSig.failed && (
                        <input
                          readOnly
                          className="mt-1 w-full rounded border border-amber-300 bg-amber-50 px-1.5 py-1 text-[11px]"
                          value={sg.sign_url ?? ''}
                          onFocus={(e) => e.currentTarget.select()}
                          ref={(el) => el?.select()}
                          title="Copying is blocked here — select and press ⌘C"
                        />
                      )}
                    </div>
                    <span
                      className={
                        sg.status === 'signed'
                          ? 'shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800'
                          : sg.status === 'declined'
                            ? 'shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800'
                            : requestsSent
                              ? 'shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800'
                              : 'shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600'
                      }
                    >
                      {sg.status === 'signed'
                        ? 'Signed ✓'
                        : sg.status === 'declined'
                          ? 'Declined'
                          : requestsSent
                            ? 'Sent'
                            : 'Not sent'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-2 text-sm">
                {c.signatures_required.map((p, i) => (
                  <li key={i}>
                    <div className="font-medium">
                      {p.name} <span className="text-gray-500">— {p.role}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-400">
                      Signature: __________________ Date: __________
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-500">
              Mediated by {c.mediator}. The mediator facilitates and does not sign.
            </p>

            {sectionHeader('Confidentiality')}
            <p className="text-xs italic text-gray-600">{c.confidentiality_notice}</p>

            {currentAgreement && !resolved && (
              <DocManagementPanel
                agreementId={currentAgreement.id}
                caseId={caseId}
                accessToken={accessToken}
                onChanged={() => {
                  getAgreement(sessionId, accessToken)
                    .then((agreement) => setState({ status: 'idle', agreement }))
                    .catch(() => undefined);
                  getSessionSigningStatus(sessionId, accessToken)
                    .then((res) => setSigning(res && res.signing.total > 0 ? res.signing : null))
                    .catch(() => undefined);
                }}
              />
            )}

            {!resolved && (
              <div className="mt-6 border-t border-surface-border pt-4">
                {signSend.error && (
                  <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
                    {signSend.error}
                  </div>
                )}
                {signGate && (
                  <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                    <div className="font-semibold">Not everyone has confirmed the draft.</div>
                    {signGate.pending.length > 0 && <div>Waiting on: {signGate.pending.join(', ')}</div>}
                    {signGate.changes.length > 0 && (
                      <div>Requested changes: {signGate.changes.join(', ')}</div>
                    )}
                    <div className="mt-1.5 flex gap-3">
                      <button
                        type="button"
                        className="font-semibold hover:underline"
                        onClick={() => void handleRequestSignatures(true)}
                        disabled={signSend.busy}
                      >
                        Send for signature anyway
                      </button>
                      <button type="button" className="hover:underline" onClick={() => setSignGate(null)}>
                        Wait
                      </button>
                    </div>
                  </div>
                )}
                {!requestsSent && (
                  <>
                    <label className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] leading-snug text-amber-900">
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={aiReviewed}
                        onChange={(e) => setAiReviewed(e.target.checked)}
                      />
                      <span>You acknowledge this stipulation was generated with AI and by checking this box you are confirming you have carefully reviewed the entire document with the Parties to confirm it is the Parties intent and that they understand it's terms. Do not select this box if you have not read and reviewed the resolution agreement in its entirety with the Parties.</span>
                    </label>
                    <button
                      type="button"
                      className="btn-primary w-full justify-center !py-2 text-sm"
                      onClick={() => void handleRequestSignatures()}
                      disabled={signSend.busy || !currentAgreement || drafting || !aiReviewed}
                    >
                      {signSend.busy ? 'Sending…' : 'Request signatures'}
                    </button>
                    <p className="mt-1 text-center text-[11px] text-gray-500">
                      Texts and emails each party their private signing link.
                    </p>
                  </>
                )}
                {requestsSent && signing && !signing.all_signed && (
                  <>
                    <button type="button" className="btn-primary w-full justify-center !py-2 text-sm" disabled>
                      Waiting for signatures ({signing.signed}/{signing.total})
                    </button>
                    <div className="mt-1.5 flex items-center justify-center gap-3 text-[11px]">
                      <button
                        type="button"
                        className="text-brand hover:underline"
                        onClick={() => void handleRequestSignatures()}
                        disabled={signSend.busy}
                      >
                        {signSend.busy ? 'Resending…' : 'Resend links'}
                      </button>
                      <span className="text-gray-400">Updates automatically as parties sign.</span>
                    </div>
                  </>
                )}
                {requestsSent && signing?.all_signed && (
                  <>
                    <div className="mb-2 rounded-md border border-surface-border bg-surface-muted/40 p-2">
                      <label className="block text-[11px] font-semibold text-gray-700">Settlement amount</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="mt-1 w-full rounded border border-surface-border px-2 py-1 text-sm tabular-nums"
                        placeholder="$0.00 — enter the agreed total"
                        value={settleAmount}
                        onChange={(e) => setSettleAmount(e.target.value)}
                      />
                      <p className="mt-0.5 text-[10px] text-gray-500">
                        {settleSource === 'ai_extracted'
                          ? 'Auto-read from the agreement — please confirm or correct before closing out.'
                          : settleSource === 'parsed_terms' || settleSource === 'recorded'
                          ? 'From the agreement terms — correct it here if needed.'
                          : 'Total the parties agreed to pay (leave blank if non-monetary).'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-primary w-full justify-center !py-2 text-sm"
                      onClick={() => void handleResolve()}
                      disabled={resolving || !currentAgreement}
                    >
                      {resolving ? 'Closing out…' : '✓ Close out mediation'}
                    </button>
                    <p className="mt-1 text-center text-[11px] text-gray-500">
                      Records the resolution and generates the Report of Mediation.
                    </p>
                  </>
                )}
              </div>
            )}
          </article>
        )}
      </div>
    </div>
  );
}

